import {VnodeDOM} from 'mithril';
import {extend, override} from 'flarum/common/extend';
import {ApiPayloadSingle} from 'flarum/common/Store';
import Model from 'flarum/common/Model';
import Post from 'flarum/common/models/Post';
import app from 'flarum/forum/app';
import Tooltip from 'flarum/common/components/Tooltip';
import CommentPost from 'flarum/forum/components/CommentPost';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import EditPostComposer from 'flarum/forum/components/EditPostComposer';
import ReplyComposer from 'flarum/forum/components/ReplyComposer';
import Embed from '../common/models/Embed';
import RichEmbed, {RichEmbedAttrs} from '../common/components/RichEmbed';
import BlockEmbed from '../common/components/BlockEmbed';
import RichImage from '../common/components/RichImage';

const EMBEDS_DONT_TRY_AGAIN: string[] = [];

/**
 * Modify the post's DOM to mount the custom Mithril link component
 * @param dom .Post-body element
 * @param post In edit mode, pass null. Otherwise post being rendered
 */
function refreshEmbeds(dom: HTMLElement, post: Post | null = null) {
    const debug = (message: string) => {
        if (app.forum.attribute('debug')) {
            console.debug('[embeds ' + (post ? '#' + post.id() : 'prev') + '] ' + message);
        }
    };

    const selector = '.rich-url, .inline-url' + (app.forum.attribute('richEmbedsImage') ? ', .inline-img' : '');

    const links = dom.querySelectorAll(selector);

    debug('refresh, ' + links.length + ' links');

    links.forEach(link => {
        // All elements should be of type HTMLAnchorElement or HTMLDivElement due to the selector, but we'll check for typescript
        if (!(link instanceof HTMLElement)) {
            return;
        }

        const url = link instanceof HTMLAnchorElement ? link.href : link.dataset.src as string;

        // If it's already rendered (and didn't change during edit), our job is already done
        if (link.dataset.embedUrl === url) {
            debug('ignore already mounted ' + url);
            return;
        }

        const embed = app.store.getById<Embed>('kilowhat-rich-embeds', url);

        if (!embed) {
            // If we are not in a preview, don't try to render new embeds
            if (post) {
                debug('ignore new url ' + url);
                return;
            }

            if (EMBEDS_DONT_TRY_AGAIN.indexOf(url) !== -1) {
                debug('debounced url ' + url);
                return;
            }

            debug('queue url ' + url);

            // We don't want to load the same URL twice at the same time (this will happen if the server doesn't use queues and it takes a while to process)
            // And this list will also prevent us querying embeds that are not allowed or that cause errors
            EMBEDS_DONT_TRY_AGAIN.push(url);

            app.request<ApiPayloadSingle>({
                method: 'POST',
                url: app.forum.attribute('apiUrl') + '/kilowhat-rich-embeds/get',
                body: {
                    url,
                },
                errorHandler(error) {
                    // Ignore our own errors, this indicates the URL cannot be embedded
                    if (error.status !== 422) {
                        throw error;
                    }
                },
            }).then(response => {
                app.store.pushPayload(response);

                // No need to use m.redraw() since everything is done via DOM manipulation anyway
                refreshEmbeds(dom, post);
            });

            return;
        }

        if (post) {
            if (post.data.relationships &&
                post.data.relationships.kilowhatRichEmbeds &&
                Array.isArray(post.data.relationships.kilowhatRichEmbeds.data)) {
                if (!post.data.relationships.kilowhatRichEmbeds.data.some(record => record.id === embed.id())) {
                    debug('embed exists but not here ' + url);
                    return;
                }
            }
        }

        // If an image is nested inside of a link, we'll skip rendering
        // This is both to avoid issues due to nesting Mithril mount points and also because when the parent is turned
        // into a Mithril component, the reference we have to the child here will no longer point to the right node
        // Theoretically you might also be able to nest links with the same issues so we'll not check for images specifically
        // This code is placed after the API call so that we still retrieve those images during preview
        // If parentElement is null, this node was probably removed from the DOM due to being a child of a link already
        if (!link.parentElement || link.parentElement.closest('.inline-url')) {
            debug('nested embed ' + url);
            return;
        }

        if (link.classList.contains('inline-img')) {
            debug('mount image ' + url);

            link.innerHTML = '';
            link.dataset.embedUrl = url;
            link.classList.add('rich-embed-img');

            m.mount(link, {
                view() {
                    return m(RichImage, {
                        embed,
                        src: link.dataset.src,
                        alt: link.dataset.alt,
                        height: link.dataset.height,
                        width: link.dataset.width,
                    });
                },
            });

            return;
        }

        const layout = app.forum.attribute<string>('richEmbedsLayout');

        if (layout === 'separated') {
            debug('ignore separated layout ' + url);
            return;
        }

        const withoutOpengraph = app.forum.attribute<boolean>('richEmbedsWithoutOpengraph');

        let imageChild: HTMLElement;

        if (link.childNodes.length === 1 && link.firstChild instanceof HTMLElement && link.firstChild.classList.contains('inline-img')) {
            imageChild = link.firstChild;
        }

        // Make copy before we clear the content for the Mithril component
        const labelHtml = link.innerHTML;

        link.innerHTML = '';
        link.dataset.embedUrl = url;
        link.classList.add('rich-embed');

        const attrs: RichEmbedAttrs = {
            layout,
            style: app.forum.attribute<string>('richEmbedsStyle'),
            withoutOpengraph,
            labelHtml,
            embed,
            canDisable: post && post.attribute<boolean>('canDisableRichEmbeds'), //: app.forum.attribute<boolean>('canDisableRichEmbeds'), // TODO: currently only allowing delete on saved post. Needs a way to reset preview when disabling during preview
            disable() {
                if (post) {
                    return post.save({
                        kilowhatRichEmbedsDisable: true,
                    }).then(() => {
                        m.redraw();
                    });
                } else {
                    // @ts-ignore cannot be properly type-hinted
                    app.composer.fields.kilowhatRichEmbedsDisable = true;

                    return Promise.resolve();
                }
            },
        };

        if (imageChild) {
            debug('child image ' + url);

            const imageEmbed = app.store.getById<Embed>('kilowhat-rich-embeds', imageChild.dataset.src);

            if (imageEmbed) {
                attrs.childImage = {
                    embed: imageEmbed,
                    src: imageChild.dataset.src,
                    alt: imageChild.dataset.alt,
                    height: imageChild.dataset.height,
                    width: imageChild.dataset.width,
                }
            }
        }

        debug('mount ' + url);

        m.mount(link, {
            view() {
                return m(RichEmbed, attrs);
            },
        });
    });
}

app.initializers.add('kilowhat-rich-embeds', () => {
    app.store.models['kilowhat-rich-embeds'] = Embed;

    extend(CommentPost.prototype, ['oncreate', 'onupdate'], function (nothing: any, vnode: VnodeDOM) {
        // If editing, this will be handled by preview below
        if (!this.isEditing() && !this.attrs.post.attribute('kilowhatRichEmbedsDisable')) {
            refreshEmbeds(vnode.dom.querySelector('.Post-body')!, this.attrs.post);
        }
    });

    extend(CommentPost.prototype, 'oninit', function () {
        this.subtree!.check(() => this.attrs.post.attribute('kilowhatRichEmbedsDisable'));
    });

    extend(CommentPost.prototype, 'content', function (content) {
        if (app.forum.attribute<string>('richEmbedsLayout') !== 'separated') {
            return;
        }

        // @ts-ignore Flarum says attrs is protected
        const post: Post = this.attrs.post;

        const embedsRelation = Model.hasMany<Embed>('kilowhatLinkRichEmbeds').call(post);

        if (!Array.isArray(embedsRelation)) {
            return;
        }

        const withoutOpengraph = app.forum.attribute<boolean>('richEmbedsWithoutOpengraph');

        const embeds: Embed[] = embedsRelation.filter(embed => embed && embed.hasContent(withoutOpengraph)) as Embed[];

        if (embeds.length === 0) {
            return;
        }

        content.push(m('.rich-embed-collection', embeds.map(embed => {
            return m('a.rich-embed', {
                href: embed.id(),
            }, m(BlockEmbed, {
                style: app.forum.attribute<string>('richEmbedsStyle'),
                withoutOpengraph,
                embed,
                canDisable: post.attribute<boolean>('canDisableRichEmbeds') && !this.isEditing(),
                disable() {
                    return post.save({
                        kilowhatRichEmbedsDisable: true,
                    }).then(() => {
                        m.redraw();
                    });
                },
            }));
        })));
    });

    let debouncePreview = 0;

    extend(s9e.TextFormatter, 'preview', function (output: any, content: string, dom: HTMLElement) {
        let canUseEmbeds: boolean;

        if (app.composer.bodyMatches(EditPostComposer)) {
            const {post} = app.composer.body.attrs;

            if (post.attribute('kilowhatRichEmbedsDisable')) {
                return;
            }

            canUseEmbeds = post.attribute('canUseRichEmbeds');
        } else {
            canUseEmbeds = app.forum.attribute('canUseRichEmbeds');
        }

        if (!canUseEmbeds) {
            return;
        }

        clearTimeout(debouncePreview);

        debouncePreview = setTimeout(() => refreshEmbeds(dom), 300) as any;
    });

    [DiscussionComposer, ReplyComposer, EditPostComposer].forEach(component => {
        extend(component, 'data', function (data) {
            data.kilowhatRichEmbedsDisable = this.composer.fields.kilowhatRichEmbedsDisable;
        });
    });

    // Workaround tooptip text change issue
    override(Tooltip.prototype, 'recreateTooltip', function (original, ...args) {
        if (this.shouldRecreateTooltip && this.childDomNode !== null) {
            const tooltipInstance = $(this.childDomNode).data('bs.tooltip');
            if (tooltipInstance && tooltipInstance.$tip) {
                // Remove transition so hide+destroy is instant
                // This class is only added in show() and never removed unless text changes, so we have to manually remove it
                tooltipInstance.$tip.removeClass('fade');
            }
        }

        original(...args);
    });
});
